import {
  apiFavoriteProfile,
  apiUnfavoriteProfile,
  fetchFavoriteProfiles,
} from "#api/account/favorites";
import { PAGINATION_LIMIT } from "#lib/pagination";
import { ElementType } from "#lib/types";
import {
  ILocalStorageSchema,
  getLocalStorageItem,
  setLocalStorageItem,
} from "#storage/local";
import { IFavoriteArtist } from "../types";
import { isRegisteredAccount } from "./auth";

interface IProfileData extends Pick<IFavoriteArtist, "service" | "id"> { }

let favoriteProfiles:
  | Awaited<ReturnType<typeof fetchFavoriteProfiles>>
  | undefined = undefined;

export async function isFavoriteProfile(service: string, profileID: string) {
  const result = await findFavoriteProfiles([{ service, id: profileID }]);

  return result.length === 0 ? false : true;
}

export async function findFavoriteProfiles(
  profilesData: IProfileData[]
): Promise<IProfileData[]> {
  const isRegistered = isRegisteredAccount();

  // return early for non-registered users
  // instead of rewriting all flows depending on it
  // since technically unregistered users won't get matches
  if (!isRegistered) {
    return [];
  }

  const favProfiles = await getFavoriteProfiles();
  const matches: IProfileData[] = [];

  for (const profileData of profilesData) {
    const fav = favProfiles.find(
      ({ id, service }) =>
        id === profileData.id && service === profileData.service
    );

    if (fav) {
      matches.push(fav);
    }
  }

  return matches;
}

async function getFavoriteProfiles() {
  const storageFavs = getLocalStorageItem("favs");

  if (!storageFavs) {
    const favs = await fetchFavoriteProfiles();
    const localFavs = favs.map<ElementType<ILocalStorageSchema["favs"]>>(
      ({ service, id }) => {
        return { service, id };
      }
    );
    setLocalStorageItem("favs", JSON.stringify(localFavs));

    return favs;
  }

  const parsedFavs: ILocalStorageSchema["favs"] = JSON.parse(storageFavs);

  return parsedFavs;
}

/**
 * A duct-tape function to return actual fav profiles
 * instead of subset for matching.
 */
export async function getAllFavoriteProfiles(
  offset: number = 0,
  order: "asc" | "desc" = "desc",
  sortBy: "updated" | "faved_seq" | "last_imported" = "updated"
) {
  if (!favoriteProfiles) {
    favoriteProfiles = await fetchFavoriteProfiles();
  }

  {
    const localFavs = favoriteProfiles.map<
      ElementType<ILocalStorageSchema["favs"]>
    >(({ service, id }) => {
      return { service, id };
    });

    setLocalStorageItem("favs", JSON.stringify(localFavs));
  }

  const compare = new Intl.Collator().compare;
  const preppedFavs = favoriteProfiles
    .filter((profile) => profile)
    .sort((prev, next) => {
      switch (sortBy) {
        case "updated": {
          return compare(prev.updated, next.updated);
        }

        case "faved_seq": {
          return prev.faved_seq === next.faved_seq
            ? 0
            : prev.faved_seq < next.faved_seq
              ? -1
              : 1;
        }

        case "last_imported": {
          return compare(prev.last_imported, next.last_imported);
        }

        default: {
          throw new Error(`Unknown sort by value "${sortBy satisfies never}".`);
        }
      }
    });

  if (order === "desc") {
    preppedFavs.reverse();
  }
  const resultFavs = preppedFavs.slice(offset, offset + PAGINATION_LIMIT);

  return {
    count: preppedFavs.length,
    profiles: resultFavs,
  };
}

export async function addProfileToFavorites(
  service: string,
  profileID: string
) {
  await apiFavoriteProfile(service, profileID);

  const favs = await fetchFavoriteProfiles();
  const localFavs = favs.map<ElementType<ILocalStorageSchema["favs"]>>(
    ({ service, id }) => {
      return { service, id };
    }
  );

  setLocalStorageItem("favs", JSON.stringify(localFavs));
  favoriteProfiles = favs;

  return { service, profileID };
}

export async function removeProfileFromFavorites(
  service: string,
  profileID: string
) {
  await apiUnfavoriteProfile(service, profileID);

  const favs = await fetchFavoriteProfiles();
  const localFavs = favs.map(({ service, id }) => {
    return { service, id };
  });

  setLocalStorageItem("favs", JSON.stringify(localFavs));
  favoriteProfiles = favs;

  return { service, profileID };
}
